import _ from 'lodash'
import { t } from '@/i18n/i18ns'
import { mapActions, mapState } from 'pinia'
import AuthMixin from '@/components/mixins/auth-mixin'
import EventBus from '@/components/mixins/event-bus-mixin'
import UnsavedDataMixin from '@/components/mixins/unsaved-data-mixin'
import { hasAccessToLEDPage } from '@/utils/led-page-accessibility.js'
import { hasAccessToVideowallPage } from '@/utils/videowall-page-accessibility.js'
import { tagsStore } from '@/pinia/stores/tagsStore'
import { TAG_TYPE_STATE_KEY } from '@/pinia/stores/storeUtils'
import { useUserStore } from '@base-shared/stores/user';

const ARYA_MENU = () => ([
  {
    id: 'menu',
    routeName: ['menuComp'],
    fullPath: '/menu',
    name: t('label.home'),
    isVisible: false,
    addClass: {},
    subMenuItems: []
  },
  {
    id: 'media',
    routeName: ['media'],
    fullPath: '/media',
    name: t('label.file', 2),
    isVisible: false,
    addClass: {},
    subMenuItems: []
  },
  {
    id: 'create',
    routeName: ['create-template-set', 'led-configuration', 'videowall', 'smartText', 'create-project-template', 'edit-project-audio', 'edit-project-template'],
    fullPath: '/create',
    name: t('label.create'),
    isVisible: false,
    addClass: { createLock: false },
    subMenuItems: [
      { id: 'templates', name: t('label.template', 2), isVisible: false, fullPath: '/create/template', routeNames: ['create-project-template', 'edit-project-template'] },
      { id: 'new', name: t('label.newProject'), isVisible: false, fullPath: '/create/new', routeNames: [] },
      { id: 'led', name: 'LED', isVisible: false, fullPath: '/led', routeNames: ['led-configuration'] },
      { id: 'videowall', name: 'Video Wall', isVisible: false, fullPath: '/videowall', routeNames: ['videowall'] },
      { id: 'audio', name: t('label.audio'), isVisible: false, fullPath: '/create/audio', routeNames: ['edit-project-audio'] },
      { id: 'text', name: t('label.text'), isVisible: false, fullPath: '/smart-text', routeNames: ['smartText'] }
    ]
  },
  {
    id: 'schedule',
    routeName: ['schedules'],
    fullPath: '/schedule',
    name: t('label.schedule'),
    isVisible: false,
    addClass: {},
    subMenuItems: []
  },
  {
    id: 'player',
    routeName: ['player', 'monitoring', 'reporting'],
    fullPath: '/player',
    name: t('label.player', 2),
    isVisible: false,
    addClass: {},
    subMenuItems: [
      { id: 'player', name: t('label.setup'), isVisible: false, fullPath: '/player', routeNames: ['player'] },
      { id: 'monitoring', name: t('label.monitoring'), isVisible: false, fullPath: '/monitoring', routeNames: ['monitoring'] },
      { id: 'reporting', name: t('label.reporting'), isVisible: false, fullPath: '/reporting', routeNames: ['reporting'] }
    ]
  },
  {
    id: 'alert',
    routeName: ['alert'],
    fullPath: '/alert',
    name: t('label.alert', 2),
    isVisible: false,
    addClass: {},
    subMenuItems: []
  }
])

const ACTIVATE_MENU = [
  {
    id: 'license-ordering',
    routeName: ['distributor'],
    fullPath: '/distributor',
    name: 'License Ordering',
    isVisible: false,
    addClass: {},
    subMenuItems: []
  },
  {
    id: 'license-activation',
    routeName: ['activation'],
    fullPath: '/activation',
    name: 'License Activation',
    isVisible: true,
    addClass: {},
    subMenuItems: []
  }
]

export default {
  name: 'side-navigation',
  inject: [
    '$currentUser',
    '$utils',
    '$dataStore',
    '$filtersRepository'
  ],
  mixins: [AuthMixin, UnsavedDataMixin],
  data () {
    return {
      activeMenuItem: null,
      activeSubMenuItem: null,
      authenticated: false,
      isActivate: false,
      isSubMenuOpen: false,
      isTOSPage: false,
      menuItems: ARYA_MENU(),
      userDbListForSmatText: {
        fetchDone: false,
        userDb: []
      },
      ledTags: [],
      videoWallTags: [],
      isHubEnabled: CONFIG.HAS_HUB
    }
  },
  computed: {
    ...mapState(tagsStore, {
      tagsStoreLedTags: 'ledTags',
      tagsStoreVideowallTags: 'videowallTags',
      tagsStoreError: 'error'
    }),
    subMenuItems () {
      return this.activeMenuItem ? this.activeMenuItem.subMenuItems : []
    },
    isSomeMenuItemVisible () {
      return this.menuItems.some((menuItem) => menuItem.isVisible)
    }
  },
  watch: {
    /**
     * Watches the `userStore.currentUser` for changes. Replacement
     * for the old `BIND_USER_DETAILS_IN_VUE` from EventBus.
     *
     * Once the user details have been bound in Vue, it's time to reference the canvas for led and videowall menus and re-evaluate menu item visibility.
     * The purpose of initializing the canvas during this event is simply to minimize tag calls and avoid backend race condition errors.
     * This event will only be triggered after signing in, refreshing the token, switching accounts, and fetching all user information from the backend, which is then bound in Vue.
     */
    'userStore.currentUser': {
      handler (newValue) {
        this.manageMenuVisibility(true)
      },
      deep: true
    }
  },
  setup () {
    // Expose the user store to the Options API context.
    return { userStore: useUserStore() };
  },
  methods: {
    ...mapActions(tagsStore, ['fetchTags']),
    addEventListeners () {
      const mainComponent = document.querySelector('.main-component')
      const navigation = document.querySelector('#navigation')

      if (mainComponent) {
        mainComponent.addEventListener('click', () => this.handleOutsideClickEvent())
      }

      if (navigation) {
        navigation.addEventListener('click', () => this.handleOutsideClickEvent())
      }
    },
    handleOutsideClickEvent () {
      if (this.isSubMenuOpen) {
        this.onSubMenuNavClose()
      }
    },
    isMenuItemHasSubMenuVisible () {
      // Sub menu is open if and only if there are sub menu items present for the active menu item AND if there are sub menu items that are visible
      const visibleSubMenu = !_.isEmpty(this.activeMenuItem.subMenuItems) ? this.activeMenuItem.subMenuItems.filter(subMenuItem => subMenuItem.isVisible) : []

      /**
       * If only 'templates' is visible, it is considered as no visible submenu.In discovery account, clicking on CREATE will redirect to the templates page
       * rather than displaying only the "Templates" submenu.
       */
      if (visibleSubMenu.length === 1 && visibleSubMenu[0].id === 'templates') {
        return false
      }

      return !_.isEmpty(visibleSubMenu)
    },
    onMenuItemClick (itemRef, routeName, fullPath) {
      const newActiveMenuItem = this.menuItems.find((menuItem) => menuItem.id === itemRef) ?? null

      if (newActiveMenuItem && this.activeMenuItem && newActiveMenuItem.id === this.activeMenuItem.id && this.isSubMenuOpen) {
        this.onSubMenuNavClose()
        return
      }

      this.activeMenuItem = newActiveMenuItem
      this.isSubMenuOpen = this.isMenuItemHasSubMenuVisible()

      if (routeName.includes(this.$route.name)) {
        return
      }

      if (!this.isSubMenuOpen) {
        // Check unsaved data before redirection
        this.checkUnsavedData({
          nextAction: () => {
            this.$filtersRepository.deleteAll()
            this.$router.push(fullPath, () => { })
            EventBus.$emit('MENU_ITEM_CLICKED', { itemRef, routeName: routeName[0], fullPath })
          }
        })
      }
    },
    onSubMenuItemClick (subMenuItemId) {
      this.checkUnsavedData({
        nextAction: () => {
          this.$filtersRepository.deleteAll()

          switch (subMenuItemId) {
            case 'templates':
              this.$router.push('/create/template', () => {})
              break
            case 'new':
              this.$dataStore.setStore({ pageRedirectAfterCreateProject: '/media', prevPage: 'createProject', prevCreateProjectType: 'custom' })
              this.$router.push('/create/new', () => {}) // Redirect to create new template page
              break
            case 'led':
              this.$router.push('/led', () => {}) // Redirect to led page
              break
            case 'videowall':
              this.$router.push('/videowall', () => {}) // Redirect to videowall page
              break
            case 'audio':
              this.$router.push('/create/audio', () => { }) // Redirect to create audio page
              break
            case 'text':
              this.$router.push('/smart-text', () => {}) // Redirect to smart-text page
              break
            case 'player':
              this.$router.push('/player', () => {}) // Redirect to player page
              break
            case 'monitoring':
              this.$router.push('/monitoring', () => {}) // Redirect to monitoring page
              break
            case 'reporting':
              this.$router.push('/reporting', () => {}) // Redirect to reporting page
              break
          }

          this.isSubMenuOpen = false
        }
      })
    },
    /**
     * On hide arrow clicked, hide Side Nav Menu and reset active menu item and sub menu item
     */
    onSubMenuNavClose () {
      this.isSubMenuOpen = false
      this.setDefaultSelected()
    },
    setDefaultSelected () {
      const currentRouteName = this.$route.name
      const currentPath = this.$route.path

      if (currentRouteName) {
        const activeMenuItem = this.menuItems.find((menuItem) => menuItem.routeName.includes(currentRouteName))

        if (activeMenuItem && activeMenuItem.isVisible) {
          this.activeMenuItem = activeMenuItem

          const activeSubMenuItem = activeMenuItem.subMenuItems.find((subMenuItem) => {
            if (currentRouteName === 'create-template-set') {
              if (currentPath.includes('/template')) {
                return subMenuItem.id === 'templates'
              } else if (currentPath.includes('/new')) {
                return subMenuItem.id === 'new'
              } else if (currentPath.includes('/audio')) {
                return subMenuItem.id === 'audio'
              }
            }

            return subMenuItem.routeNames.includes(currentRouteName)
          })

          if (activeSubMenuItem && activeSubMenuItem.isVisible) {
            this.activeSubMenuItem = activeSubMenuItem
          } else {
            this.activeSubMenuItem = null
          }
        } else {
          this.activeMenuItem = null
          this.activeSubMenuItem = null
        }
      }
    },
    async setMenuItemVisibility () {
      let menuItems = this.$utils.clone(this.menuItems)

      if (this.isActivate) {
        if (this.$currentUser.hasGroupDistributor()) {
          menuItems = menuItems.map((item) => {
            item.isVisible = true
            return item
          })
        }
      } else {
        for (const item of menuItems) {
          // Always show Home menu button when hub is enabled
          if (item.id === 'menu') {
            item.isVisible = this.isHubEnabled
            continue
          }

          // Reset 'createLock' value to reflect correct create icon when switching accounts
          if (item.id === 'create') {
            item.addClass.createLock = false
          }

          if (this.$currentUser.isStandAloneUser() || this.$currentUser.isFreeUser()) {
            item.isVisible = true
            if (item.id === 'alert') item.isVisible = false
          } else if (this.$currentUser.hasGroupUserAdmin()) {
            item.isVisible = true
          } else if (!this.$currentUser.hasGroupUserAdmin() && this.$currentUser.getUserGroupId() !== null) {
            const create = this.$currentUser.hasGroupUserCreate()
            const createLock = this.$currentUser.hasGroupUserCreateLock()
            const schedule = this.$currentUser.hasGroupUserSchedule()
            const player = this.$currentUser.hasGroupUserPlayer()
            const alert = this.$currentUser.hasGroupUserAlert()
            const modify = this.$currentUser.hasUserRightModify()
            const enterpriseWithSchedule = schedule && this.$currentUser.isEnterpriseUser() // to show LED and Videowall

            item.isVisible = false

            if (item.id === 'create' && (create || createLock || enterpriseWithSchedule)) item.isVisible = true
            if (item.id === 'schedule' && (schedule)) item.isVisible = true
            if (item.id === 'player' && (player)) item.isVisible = true
            if (item.id === 'alert' && (alert)) item.isVisible = true
            if (item.id === 'media' && (modify || schedule)) item.isVisible = true

            // Add class to change icon
            if (item.id === 'create' && createLock) item.addClass.createLock = true
          } else if (this.$currentUser.hasGroupEnterprise()) {
            item.isVisible = true

            // Add class to change icon
            if (item.id === 'create' && this.$currentUser.hasGroupUserCreateLock()) item.addClass.createLock = true
          }

          // (global right) If there is a user_limited right, files button is visible but create button should not be visible in menu
          if (this.$currentUser.hasGroupUserLimited()) {
            if (item.id === 'create') item.isVisible = false
          }
        }
      }

      this.menuItems = menuItems
    },
    async setCanvasData () {
      await this.fetchTags([TAG_TYPE_STATE_KEY.LED_TAGS, TAG_TYPE_STATE_KEY.VIDEOWALL_TAGS])

      if (this.tagsStoreError) {
        this.clearCanvasData()
        return
      }

      this.ledTags = this.tagsStoreLedTags
      this.videoWallTags = this.tagsStoreVideowallTags
    },
    setSubMenuItemVisibility () {
      const currentRoute = this.$route

      if (currentRoute.path) {
        if (currentRoute.path.includes('/template')) {
          this.$dataStore.setStore({ prevCreateProjectType: 'default-template' })
        } else if (currentRoute.path.includes('/new')) {
          this.$dataStore.setStore({ prevCreateProjectType: 'custom' })
        }
      }

      this.menuItems.forEach((menuItem) => {
        if (menuItem.id === 'create') {
          menuItem.subMenuItems.forEach(async (subMenuItem) => {
            switch (subMenuItem.id) {
              case 'templates':
                /* Set Templates Button visibility
                * Templates button should not visible if:
                * - This component is not for media files
                * - User with schedule rights and no creates rigths nor lock_create rights
                */
                subMenuItem.isVisible = true
                if (!this.$currentUser.hasUserRightModify()) subMenuItem.isVisible = false
                if (this.$currentUser.hasGroupUserLimited()) subMenuItem.isVisible = false
                break
              case 'new':
                /* Set Custom Project Button visibility
                * Custom button should not visible if:
                * - This component is not for media files
                * - User not extended || not enterprise AND not user_create OR user_admin
                * - User create lock rights is enabled
                */
                subMenuItem.isVisible = false
                if (this.$currentUser.hasGroupUserLimited()) {
                  subMenuItem.isVisible = false
                } else if (this.$currentUser.hasUserRightCreate()) {
                  subMenuItem.isVisible = true
                }
                break
              case 'led':
                subMenuItem.isVisible = hasAccessToLEDPage(this.$currentUser, this.ledTags)
                break
              case 'videowall':
                subMenuItem.isVisible = hasAccessToVideowallPage(this.$currentUser, this.videoWallTags)
                break
              case 'audio':
                /* Set Audio Button visibility
                * Custom button should not visible if:
                * - This component is not for media files
                * - User not extended || not enterprise AND not user_create, user_create_lock OR user_admin
                * - User create lock rights is enabled
                */
                subMenuItem.isVisible = false
                if (this.$currentUser.hasGroupUserLimited()) {
                  subMenuItem.isVisible = false
                } else if (this.$currentUser.hasGroupExtended() && this.$currentUser.hasUserRightModify()) {
                  subMenuItem.isVisible = true
                }
                break
              case 'text':
                // Smart Text
                subMenuItem.isVisible = false
                if (this.$currentUser.hasUserRightSmartText() && this.userDbListForSmatText.userDb?.length) {
                  subMenuItem.isVisible = true
                }
                break
            }
          })
        } else if (menuItem.id === 'player') {
          const arePlayerSubMenusVisible = menuItem.isVisible && this.$currentUser.hasUserRightMonitoring() && this.$currentUser.hasUserRightReporting()
          menuItem.subMenuItems.forEach((subMenuItem) => {
            subMenuItem.isVisible = arePlayerSubMenusVisible
          })
        }
      })

      this.setDefaultSelected()
    },
    setSmartTextSubMenuItemVisibility (userDbListForSmatText) {
      /*
      * - User should be extended || enterprise AND user_create, user_create_lock OR user_admin
      * - User database is not empty
      */
      const createMenu = this.menuItems.find((menuItem) => menuItem.id === 'create')
      createMenu.subMenuItems.forEach((subMenuItem) => {
        if (subMenuItem.id === 'text') {
          subMenuItem.isVisible = false
          if (this.$currentUser.hasUserRightSmartText() && userDbListForSmatText.userDb?.length) {
            subMenuItem.isVisible = true
          }
        }
      })
    },
    async manageMenuVisibility (initializeCanvasData) {
      /**
       * If canvas data needs initialization, ensure it's set first before adjusting menu item visibility, as the canvas data is essential for the LED and Video wall submenus.
       */
      if (initializeCanvasData) {
        // NOTE: we don't need canvas data for Activate
        if (!this.$utils.isPageForLicenses()) {
          await this.setCanvasData()
        }
        this.setMenuItemVisibility()
        this.setSubMenuItemVisibility()
      } else {
        this.setMenuItemVisibility()
        this.setSubMenuItemVisibility()
      }
    },
    clearCanvasData () {
      this.ledTags = []
      this.videoWallTags = []
    }
  },
  mounted () {
    /**
     * When a SIGNED_IN event is triggered, the menu visibility needs to be promptly updated to ensure a quick response in the UI.
     * This event is typically triggered in every component to verify user authentication.
     * During this event, tags should not be fetched as it will retrieve the same data upon route changes and subsequent component mounting that cause to backend race condition errors.
     */
    EventBus.$on('SIGNED_IN', () => {
      if (this.$utils.isPageForLicenses()) {
        this.isActivate = true
        this.menuItems = ACTIVATE_MENU
      }
      this.authenticated = true
      this.isTOSPage = this.$route.name === 'tos'
      this.manageMenuVisibility(false)
    })

    EventBus.$on('SIGNED_OUT', () => {
      this.authenticated = false
      this.isSubMenuOpen = false
      this.activeMenuItem = null
      this.activeSubMenuItem = null
      this.clearCanvasData()
    })

    EventBus.$on('USERDB_FETCHED', (payload) => {
      this.userDbListForSmatText = payload
    })

    this.addEventListeners()
  }
}
